import * as React from "react";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import {
  Edit as EditIcon,
  Add as AddIcon,
  ChevronLeft,
  ChevronRight,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { message, Modal } from "antd";
import { API_BASE_URL, getAuthHeaders } from "../API/Api";
import { fetchProductsPage, normalizeProductRow } from "./productUtils";

const GetProducts = () => {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPaginationModel((prev) =>
        prev.page === 0 ? prev : { ...prev, page: 0 },
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const getApiProducts = async () => {
      setLoading(true);
      try {
        const { items, total } = await fetchProductsPage({
          page: paginationModel.page + 1,
          perPage: paginationModel.pageSize,
          search: debouncedSearch,
          signal: controller.signal,
        });
        if (cancelled) return;
        const startIndex = paginationModel.page * paginationModel.pageSize;
        setDataSource(
          items.map((item, index) =>
            normalizeProductRow(item, startIndex + index),
          ),
        );
        setRowCount(total);
      } catch (error) {
        if (error?.name === "AbortError" || cancelled) return;
        console.error("Error fetching products:", error);
        setDataSource([]);
        setRowCount(0);
        message.error("Failed to load products");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    getApiProducts();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    debouncedSearch,
    reloadKey,
  ]);

  const handleRemoveProduct = (id) => {
    Modal.confirm({
      title: "Remove Product",
      content: "Are you sure you want to remove this product?",
      okText: "Remove",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      async onOk() {
        try {
          const res = await fetch(`${API_BASE_URL}/admin/products/${id}`, {
            method: "DELETE",
            headers: getAuthHeaders(),
          });

          if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
          }

          if (dataSource.length <= 1 && paginationModel.page > 0) {
            setPaginationModel((prev) => ({
              ...prev,
              page: prev.page - 1,
            }));
          } else {
            setReloadKey((prev) => prev + 1);
          }
          message.success("Product removed successfully");
        } catch (error) {
          console.error("Error removing product:", error);
          message.error("Failed to remove product");
        }
      },
    });
  };

  const columns = [
    { field: "index", headerName: "#", width: 70 },
    { field: "name", headerName: "Name", width: 240 },
    { field: "code", headerName: "Code", width: 110 },
    { field: "unit_barcode", headerName: "Barcode", width: 150 },

    {
      field: "image_url",
      headerName: "Image",
      width: 110,
      renderCell: (params) =>
        params.row.image_url ? (
          <Box
            component="img"
            src={params.row.image_url}
            alt={params.row.name}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            No Image
          </Typography>
        ),
    },
    { field: "brand_name", headerName: "Brand", width: 160 },
    { field: "category_name", headerName: "Category", width: 160 },
    {
      field: "is_active",
      headerName: "Status",
      width: 130,
      renderCell: (params) =>
        params.row.is_active ? (
          <Typography sx={{ color: "#3A6F43", fontWeight: 700 }}>
            Active
          </Typography>
        ) : (
          <Typography sx={{ color: "#d32f2f", fontWeight: 700 }}>
            Inactive
          </Typography>
        ),
    },
    {
      field: "action",
      headerName: "Actions",
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Link to={`/admin/products/edit/${params.row.id}`}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              sx={{
                backgroundColor: "#3A6F43",
                color: "#fff",
                minWidth: 40,
                px: 1.5,
                "&:hover": { backgroundColor: "#3A6F43" },
              }}
            />
          </Link>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => handleRemoveProduct(params.row.id)}
          />
        </Box>
      ),
    },
  ];

  const start = paginationModel.page * paginationModel.pageSize;
  const end = start + dataSource.length;

  return (
    <div style={{ margin: "25px", overflow: "hidden" }}>
      <Box
        sx={{
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search by name, code or  barcode"
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 250,
            maxWidth: 400,
            backgroundColor: "#f5f5f5",
          }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Link to="/admin/products/add" style={{ textDecoration: "none" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: "#3f51b5",
              color: "#fff",
              "&:hover": { backgroundColor: "#303f9f" },
              borderRadius: "6px",
              padding: "10px 20px",
              whiteSpace: "nowrap",
            }}
          >
            Add Product
          </Button>
        </Link>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 300,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#3f51b5" }} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Products...
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            width: "100%",
            mb: 10,
            p: 2,
            overflowX: "auto",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              p: 1,
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                Rows per page:
              </Typography>
              <Select
                value={paginationModel.pageSize}
                onChange={(e) =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    pageSize: e.target.value,
                    page: 0,
                  }))
                }
                size="small"
                sx={{ height: "36px", minWidth: "80px" }}
              >
                {[10, 20, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Typography variant="body2">
              {rowCount === 0 ? "0 of 0" : `${start + 1}-${end} of ${rowCount}`}
            </Typography>

            <Box>
              <IconButton
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: Math.max(prev.page - 1, 0),
                  }))
                }
                disabled={paginationModel.page === 0}
                size="small"
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: prev.page + 1,
                  }))
                }
                disabled={
                  (paginationModel.page + 1) * paginationModel.pageSize >=
                  rowCount
                }
                size="small"
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ height: 640, width: "100%" }}>
            <DataGrid
              rows={dataSource}
              columns={columns}
              disableSelectionOnClick
              hideFooter
              rowHeight={56}
              sx={{
                height: "100%",
                minWidth: 1400,
                border: 0,
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #e0e0e0",
                },
              }}
            />
          </Box>
        </Paper>
      )}
    </div>
  );
};

export default GetProducts;
